import React, { type FunctionComponent, useState, useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import styled from '@emotion/styled'
import isEqual from 'lodash/isEqual'
import {
  Button,
  fontSize,
  fontWeight,
  Modal,
  RadioWithLabel,
  spacing,
  notify,
} from '@retailer-platform/shared-components'
import { ApprovedIcon, IssueIcon } from '@instacart/ids-core'
import { useCallback } from 'react'
import { useUpdateRetailerProductVisibilitiesMutation } from 'domains/products/api'
import { NotInAssortmentDatePicker } from './components/NotInAssortmentDatePicker'

interface EditInAssortmentModalProps {
  retailerId: string
  sampleProduct?: {
    name: string
    upc: string
  }
  productIds: string[]
  initialSettings?: {
    inAssortment: boolean
    outOfAssortmentStartAt: string
    outOfAssortmentEndAt: string
  }
  onClose?: () => void
}

const ProductInfoContainer = styled.div({
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  alignItems: 'center',
  paddingBottom: '20px',
  borderBottom: '1px solid #E8E9EB',
})

const OptionsContainer = styled.div({
  display: 'flex',
  flex: 1,
  minHeight: '100px',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  gap: spacing.X8,
})

const Description = styled.div({
  width: '90%',
})

const ProductsList = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.X8,
})

const ProductRow = styled.div({
  display: 'flex',
  flexDirection: 'row',
  gap: spacing.X64,
})

const ProductsListHeader = styled.div({
  width: '250px',
  fontSize: fontSize.X12,
})

const ProductInfo = styled.div({
  width: '250px',
  fontWeight: fontWeight.BOLD,
})

const OptionTitle = styled.div({
  fontWeight: fontWeight.BOLD,
  width: '90%',
})

const Options = styled.div({
  fontWeight: fontWeight.BOLD,
  width: '90%',
  marginBottom: spacing.X12,
})

const toStateMap = input => {
  if (input == null) {
    return {
      inAssortment: null,
      notInAssortmentOngoing: null,
      outOfAssortmentStartAt: null,
      outOfAssortmentEndAt: null,
    }
  }
  const startAt = Date.parse(input.outOfAssortmentStartAt)
  const endAt = Date.parse(input.outOfAssortmentEndAt)
  return {
    inAssortment: input.inAssortment,
    notInAssortmentOngoing:
      !input.inAssortment &&
      input.outOfAssortmentStartAt == null &&
      input.outOfAssortmentEndAt == null,
    outOfAssortmentStartAt: isNaN(startAt) ? null : input.outOfAssortmentStartAt,
    outOfAssortmentEndAt: isNaN(endAt) ? null : input.outOfAssortmentEndAt,
  }
}

export const EditInAssortmentModal: FunctionComponent<EditInAssortmentModalProps> = ({
  retailerId,
  sampleProduct,
  productIds,
  initialSettings,
  onClose,
}) => {
  const initialValues = toStateMap(initialSettings)

  const [values, setValues] = useState(initialValues)
  const [updateRetailerProductVisibilitiesMutation, { data, loading, error }] =
    useUpdateRetailerProductVisibilitiesMutation()

  useEffect(() => {
    if (!error && !loading && data) {
      notify({
        contents: (
          <React.Fragment>
            <ApprovedIcon size={15} color="systemSuccessRegular" css={{ marginRight: '5px' }} />
            <FormattedMessage id={'productsDomain.search.edit.inAssortment.updateSuccess'} />
          </React.Fragment>
        ),
        timeout: 5000,
      })
      onClose()
    } else if (error && !loading) {
      notify({
        contents: (
          <React.Fragment>
            <IssueIcon size={15} color="systemDetrimentalRegular" css={{ marginRight: '5px' }} />
            <FormattedMessage id={'productsDomain.search.edit.inAssortment.updateError'} />
          </React.Fragment>
        ),
        timeout: 5000,
      })
    }
  }, [error, loading, data, onClose])

  const onSubmit = useCallback(() => {
    const entityIds = productIds.map(productId => ({
      retailerProductId: {
        retailerId: retailerId,
        productId: productId,
      },
    }))
    const data = {
      entityId: entityIds,
      inAssortment: values.inAssortment,
      outOfAssortmentStartAt: values.notInAssortmentOngoing ? null : values.outOfAssortmentStartAt,
      outOfAssortmentEndAt: values.notInAssortmentOngoing ? null : values.outOfAssortmentEndAt,
    }
    updateRetailerProductVisibilitiesMutation({
      variables: {
        data,
      },
    })
  }, [productIds, retailerId, updateRetailerProductVisibilitiesMutation, values])

  const header = <FormattedMessage id="productsDomain.search.edit.inAssortment.title" />
  const body = (
    <div>
      <ProductInfoContainer>
        <Description>
          <FormattedMessage id="productsDomain.search.edit.inAssortment.description" />
        </Description>
        <br />
        {productIds.length > 1 || !sampleProduct ? (
          <Description>
            <FormattedMessage
              id="productsDomain.search.edit.inAssortment.productCount"
              values={{ count: productIds.length }}
            />
          </Description>
        ) : (
          <ProductsList>
            <ProductRow>
              <ProductsListHeader>
                <FormattedMessage id="productsDomain.search.edit.inAssortment.productDetail" />
              </ProductsListHeader>
              <ProductsListHeader>
                <FormattedMessage id="productsDomain.search.edit.inAssortment.upc" />
              </ProductsListHeader>
            </ProductRow>
            <ProductRow>
              <ProductInfo>{sampleProduct.name}</ProductInfo>
              <ProductInfo>{sampleProduct.upc}</ProductInfo>
            </ProductRow>
          </ProductsList>
        )}
      </ProductInfoContainer>
      <br />
      <OptionsContainer>
        <OptionTitle>In Assortment</OptionTitle>
        <Options>
          <RadioWithLabel
            id="in-assortment-yes"
            label={<FormattedMessage id="productsDomain.search.yes" />}
            value={'true'}
            checked={values.inAssortment == true}
            onChange={() =>
              setValues({
                ...values,
                inAssortment: true,
              })
            }
          />
          <RadioWithLabel
            id="in-assortment-no"
            label={<FormattedMessage id="productsDomain.search.no" />}
            value={'false'}
            checked={values.inAssortment == false}
            onChange={() =>
              setValues({
                ...values,
                inAssortment: false,
              })
            }
          />
        </Options>
        {values.inAssortment == false && (
          <React.Fragment>
            <OptionTitle>Not In Assortment Effective Dates</OptionTitle>
            <Options>
              <RadioWithLabel
                id="not-in-assortment-ongoing-yes"
                label={<FormattedMessage id="productsDomain.search.edit.inAssortment.ongoing" />}
                value={'true'}
                checked={values.notInAssortmentOngoing == true}
                onChange={() =>
                  setValues({
                    ...values,
                    notInAssortmentOngoing: true,
                  })
                }
              />
              <RadioWithLabel
                id="not-in-assortment-ongoing-no"
                label={
                  <FormattedMessage id="productsDomain.search.edit.inAssortment.selectDateRange" />
                }
                value={'false'}
                checked={values.notInAssortmentOngoing == false}
                onChange={() =>
                  setValues({
                    ...values,
                    notInAssortmentOngoing: false,
                  })
                }
              />
            </Options>
          </React.Fragment>
        )}
        {values.inAssortment == false && values.notInAssortmentOngoing == false && (
          <React.Fragment>
            <NotInAssortmentDatePicker
              startTime={values.outOfAssortmentStartAt}
              endTime={values.outOfAssortmentEndAt}
              onTimeChange={(start, end) => {
                setValues({
                  ...values,
                  outOfAssortmentStartAt: start,
                  outOfAssortmentEndAt: end,
                })
              }}
            />
          </React.Fragment>
        )}
      </OptionsContainer>
    </div>
  )
  const footer = (
    <Button
      disabled={values.inAssortment == null || isEqual(initialValues, values) || loading}
      onClick={onSubmit}
      aria-label={'save-button'}
    >
      <FormattedMessage id="action.save" />
    </Button>
  )

  return (
    <Modal
      onRequestClose={onClose}
      isOpen={true}
      headerContent={header}
      bodyContent={body}
      footerContent={footer}
      maxWidth={700}
      maxHeight={800}
    />
  )
}
